import { useCallback, useState } from "react";

const STORAGE_KEY = "edify-people-search-history";
const MAX_ITEMS = 8;

export type HistoryEntry = {
  term: string;
  fromInterpret: boolean;
};

const readHistory = (): HistoryEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((x: unknown): HistoryEntry | null => {
        if (typeof x === "string") {
          return { term: x, fromInterpret: false };
        }
        if (x && typeof x === "object" && "term" in x && typeof (x as any).term === "string") {
          return {
            term: (x as any).term,
            fromInterpret: Boolean((x as any).fromInterpret),
          };
        }
        return null;
      })
      .filter((e): e is HistoryEntry => e !== null);
  } catch {
    return [];
  }
};

export const useSearchHistory = () => {
  const [history, setHistory] = useState<HistoryEntry[]>(readHistory);

  const addToHistory = useCallback((term: string, fromInterpret = false) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    const current = readHistory();
    const filtered = current.filter((e) => e.term.toLowerCase() !== trimmed.toLowerCase());
    const next: HistoryEntry[] = [{ term: trimmed, fromInterpret }, ...filtered].slice(0, MAX_ITEMS);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setHistory(next);
    } catch {
      // ignore
    }
  }, []);

  const clearHistory = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHistory([]);
    } catch {
      // ignore
    }
  }, []);

  const refreshHistory = useCallback(() => {
    setHistory(readHistory());
  }, []);

  return { history, addToHistory, clearHistory, refreshHistory };
};
